import React from "react";
import { getProfileName, ThemeWrapper, ViewDetailsModal } from "@verity/components";
import { CustomSettingData } from "../../settings-page";
import i18n from "../../../../i18n";
import { CoreButton, CoreModal, CoreSpinner } from "@verity/components/core";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { ExtraSettingsInfo } from "../../../webpages/settings-root";
import apiClient from "../../../../helpers/api-client";
import { isEnforcedProfile } from "@verity/proctoring-option";
import { ProfileTable } from "../shared/profile-table";
import { ProfileSearchSelect } from "../shared/profile-search-select";
import { UseFetchProctoringDataReturn } from "../useFetchProctoringData";

export const DefaultProfileInput = (
	props: {
		loadData: { id: number | null };
		setting: CustomSettingData<{ id: number | null }>;
		settingsInfo?: Pick<
			Partial<ExtraSettingsInfo>,
			"Info-instituteProfiles" | "Info-instituteSettings" | "Info-optionRelatedInstituteSettings"
		>;
		onSettingChange: (value?: { id: number | null }) => void;
		disabled?: boolean;
	} & UseFetchProctoringDataReturn,
) => {
	const [searchInput, setSearchInput] = React.useState("");

	const [viewProfileModalOpen, setViewProfileModalOpen] = React.useState(false);
	const [defaultOverrideModalOpen, setDefaultOverrideModalOpen] = React.useState(false);

	const [profiles] = React.useState(() => {
		const instituteProfiles = props.settingsInfo?.["Info-instituteProfiles"] ?? [];
		const enforcedProfiles = instituteProfiles.filter((x) => isEnforcedProfile(x));
		if (enforcedProfiles.length > 0) {
			return enforcedProfiles;
		} else {
			return instituteProfiles;
		}
	});

	const [selectedProfile, setSelectedProfile] = React.useState<ProctoringOptionProfileV2>();
	const [defaultProfile, setDefaultProfile] = React.useState<ProctoringOptionProfileV2 | undefined>(() => {
		if (props.setting.value === undefined && props.loadData.id !== null) {
			return profiles.find((x) => x.id === props.loadData.id);
		} else if (props.setting.value?.id !== null) {
			return profiles.find((x) => x.id === props.setting.value?.id);
		} else {
			return undefined;
		}
	});

	const optionRelatedInstituteSettings = props.settingsInfo?.["Info-optionRelatedInstituteSettings"];

	const verityInstituteSettings = props.settingsInfo?.["Info-instituteSettings"];

	if (!verityInstituteSettings) {
		return <CoreSpinner />;
	}

	return (
		<ThemeWrapper>
			<div
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					placeContent: "center",
					border: "1px solid #e4e4e4",
					borderRadius: "8px",
					padding: "8px 24px",
					gap: "12px",
					marginBottom: "14px",
				}}
			>
				<ProfileSearchSelect
					profiles={defaultProfile ? profiles.filter((x) => x.id !== defaultProfile.id) : profiles}
					selectedProfile={selectedProfile}
					setSelectedProfile={setSelectedProfile}
					searchInput={searchInput}
					setSearchInput={setSearchInput}
					onClickSelect={(p) => {
						if (p) {
							if (defaultProfile) {
								setDefaultOverrideModalOpen(true);
							} else {
								setDefaultProfile(p);
								props.onSettingChange({ id: p.id });
								setSearchInput("");
								setSelectedProfile(undefined);
							}
						}
					}}
				/>
				<CoreButton
					onClick={() => {
						let selected = selectedProfile;
						if (searchInput !== "" && (selectedProfile?.name ?? "")?.toLowerCase() !== searchInput.toLowerCase()) {
							selected = profiles.find(
								(x) => searchInput !== "" && x.name?.toLowerCase() === searchInput.toLowerCase(),
							);
						}
						if (selected) {
							if (defaultProfile) {
								setSelectedProfile(selected);
								setDefaultOverrideModalOpen(true);
							} else {
								setDefaultProfile(selected);
								props.onSettingChange({ id: selected.id });
								setSelectedProfile(undefined);
							}
						}
					}}
				>
					Add
				</CoreButton>
			</div>

			<ProfileTable
				displayTable={Boolean(defaultProfile)}
				profiles={defaultProfile ? [defaultProfile] : []}
				emptyTableHeaderText={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.emptyProfile")}
				hidePagination
				dropdownOptions={[
					{
						name: "View",
						onClick: () => {
							setViewProfileModalOpen(true);
						},
					},
					{
						name: "Remove",
						onClick: () => {
							setDefaultProfile(undefined);
							props.onSettingChange({ id: null });
						},
					},
				]}
				verityInstituteSettings={verityInstituteSettings}
			/>

			{viewProfileModalOpen && defaultProfile && (
				<ViewDetailsModal
					profile={defaultProfile}
					title={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.viewProfile", {
						name: getProfileName(defaultProfile),
					})}
					configuration={{
						optionRelatedInstituteSettings,
						verityInstituteSettings,
						proctoringOption: defaultProfile.proctoringOption,
						proctoringData: { applications: props.applications, extensions: props.extensions },
					}}
					onClose={() => setViewProfileModalOpen(false)}
				/>
			)}

			{defaultOverrideModalOpen && defaultProfile && selectedProfile && (
				<CoreModal
					title={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.replaceProfile")}
					open={defaultOverrideModalOpen}
					onClose={() => setDefaultOverrideModalOpen(false)}
					maxWidth="564px"
					buttons={[
						{
							text: "No",
							color: "secondary",
							onClick: () => setDefaultOverrideModalOpen(false),
						},
						{
							text: "Yes",
							color: "primary",
							onClick: () => {
								setDefaultProfile(selectedProfile);
								props.onSettingChange({ id: selectedProfile.id });
								setSelectedProfile(undefined);
								setDefaultOverrideModalOpen(false);
							},
						},
					]}
				>
					<div>
						{i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.replaceProfilePrompt", {
							profile: selectedProfile.name ?? "a different profile",
						})}
					</div>
					<div style={{ marginTop: "9px" }}>
						{i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.replaceProfileContinue")}
					</div>
				</CoreModal>
			)}
		</ThemeWrapper>
	);
};

export async function saveDefaultProfile(defaultProfileId?: { id: number | null }) {
	try {
		if (defaultProfileId) {
			await apiClient.proctoringProfile.setDefault(defaultProfileId?.id ?? null);
		}
		return { ok: true };
	} catch (error) {
		console.error(error);
		return { ok: false, reason: i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.notSave") };
	}
}
